import React, { useEffect, useState } from 'react';
import { getRoomPrices, createRoomPrice, updateRoomPrice, deleteRoomPrice } from '../../../services/roomPriceApi';

// تابع برای فرمت کردن عدد با جداکننده هزارگان
const formatNumber = (num) => {
  if (!num) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// تابع برای حذف جداکننده و تبدیل به عدد
const unformatNumber = (str) => {
  if (!str) return '';
  return str.toString().replace(/,/g, '');
};

function RoomPrice() {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    roomType: '',
    price: '',
    priceDisplay: '',
    description: ''
  });

  useEffect(() => {
    fetchPrices();
  }, []);

  const fetchPrices = async () => {
    try {
      setLoading(true);
      const response = await getRoomPrices();
      setPrices(response.data || []);
      setError(null);
    } catch (err) {
      setError('خطا در دریافت اطلاعات');
      console.error('Error fetching prices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'price') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({
        ...prev,
        price: numericValue,
        priceDisplay: formatNumber(numericValue)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      roomType: '',
      price: '',
      priceDisplay: '',
      description: ''
    });
    setEditingId(null);
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingId(item._id);
      setFormData({
        roomType: item.roomType,
        price: item.price.toString(),
        priceDisplay: formatNumber(item.price),
        description: item.description || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.roomType || !formData.price) {
      setError('نوع اتاق و قیمت الزامی هستند');
      return;
    }

    try {
      const data = {
        roomType: formData.roomType,
        price: Number(formData.price),
        description: formData.description
      };

      if (editingId) {
        await updateRoomPrice(editingId, data);
      } else {
        await createRoomPrice(data);
      }
      
      closeModal();
      fetchPrices();
    } catch (err) {
      setError(err.response?.data?.msg || 'خطا در ذخیره اطلاعات');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این مورد اطمینان دارید؟')) {
      return;
    }

    try {
      await deleteRoomPrice(id);
      fetchPrices();
    } catch (err) {
      setError('خطا در حذف اطلاعات');
    }
  };

  const formatPrice = (price) => {
    if (!price) return '۰';
    return formatNumber(price);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  return (
    <div className="p-4 page-ani">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">مدیریت قیمت اتاق‌ها</h1>
            <p className="text-sm text-gray-400">تعریف و مدیریت قیمت انواع اتاق‌ها</p>
          </div>
        </div>
        <button 
          className="btn btn-primary gap-2 shadow-lg hover:scale-105 transition-transform"
          onClick={() => openModal()}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          افزودن قیمت جدید
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-error mb-4 shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl p-4 border border-primary/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/30 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-400">تعداد انواع اتاق</p>
              <p className="text-2xl font-bold text-white">{prices.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-success/20 to-success/5 rounded-xl p-4 border border-success/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/30 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-400">ارزان‌ترین قیمت</p>
              <p className="text-2xl font-bold text-white">
                {prices.length > 0 ? formatPrice(Math.min(...prices.map(p => p.price))) : '۰'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-warning/20 to-warning/5 rounded-xl p-4 border border-warning/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-warning/30 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-400">گران‌ترین قیمت</p>
              <p className="text-2xl font-bold text-white">
                {prices.length > 0 ? formatPrice(Math.max(...prices.map(p => p.price))) : '۰'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-base-300/50 rounded-xl shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr className="bg-base-200">
                <th className="text-right font-bold">نوع اتاق</th>
                <th className="text-right font-bold">قیمت (تومان)</th>
                <th className="text-right font-bold">توضیحات</th>
                <th className="text-center font-bold">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {prices.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <p className="text-gray-400 text-lg">هیچ قیمتی ثبت نشده است</p>
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => openModal()}
                      >
                        افزودن اولین قیمت
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                prices.map((item, index) => (
                  <tr key={item._id} className="hover:bg-base-200/50 transition-colors">
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary/30 to-secondary/30 rounded-lg flex items-center justify-center">
                          <span className="text-lg font-bold">{index + 1}</span>
                        </div>
                        <span className="font-medium text-white">{item.roomType}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="text-success font-bold text-lg">{formatPrice(item.price)}</span>
                        <span className="text-xs text-gray-400">تومان</span>
                      </div>
                    </td>
                    <td>
                      <span className="text-gray-300">{item.description || '-'}</span>
                    </td>
                    <td>
                      <div className="flex justify-center gap-2">
                        <button
                          className="btn btn-sm btn-info btn-outline hover:btn-info"
                          onClick={() => openModal(item)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          className="btn btn-sm btn-error btn-outline hover:btn-error"
                          onClick={() => handleDelete(item._id)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="modal modal-open">
          <div className="modal-box bg-base-200 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 className="font-bold text-xl text-white">
                {editingId ? 'ویرایش قیمت' : 'افزودن قیمت جدید'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-white font-medium">نوع اتاق *</span>
                </label>
                <input
                  type="text"
                  name="roomType"
                  value={formData.roomType}
                  onChange={handleInputChange}
                  className="input input-bordered w-full bg-base-100 focus:input-primary"
                  placeholder="مثال: اتاق یک خوابه"
                  required
                />
              </div>

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-white font-medium">قیمت (تومان) *</span>
                </label>
                <input
                  type="text"
                  name="price"
                  value={formData.priceDisplay}
                  onChange={handleInputChange}
                  className="input input-bordered w-full bg-base-100 focus:input-primary"
                  placeholder="قیمت به تومان (مثال: ۵۰,۰۰۰,۰۰۰)"
                  required
                />
              </div>

              <div className="form-control mb-6">
                <label className="label">
                  <span className="label-text text-white font-medium">توضیحات</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="textarea textarea-bordered w-full bg-base-100 focus:textarea-primary"
                  placeholder="توضیحات اختیاری"
                  rows={3}
                />
              </div>

              <div className="modal-action">
                <button type="submit" className="btn btn-primary gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {editingId ? 'بروزرسانی' : 'ثبت'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={closeModal}>
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoomPrice;
